import categoryMappings from './helpers/expert-category-mappings.json';

import { AdvizedTest } from '../../types';
import { MetaDataContext } from './types';

type Props = {
  data: {
    test: AdvizedTest;
  };
};

const expertTest = ({ data }: Props, { t, market }: MetaDataContext) => {
  if (!data) {
    return {};
  }

  const { test } = data;
  const { category } = data.test.product || {};

  if (!test) return {};

  const title =
    test.metaTitle?.replace('Advized.se', t('meta', 'site.name')) ||
    t('meta', 'expert.test-title', {
      title: test.product?.name || '',
    });

  const description =
    test.metaDescription ||
    t('meta', 'expert.test-description', {
      category: category?.name
        ? `${categoryMappings.singularDeterminates[category?.name] ||
            category?.name.toLowerCase()} `
        : '',
      title: test.product?.name || '',
    });

  const openGraph = [
    { key: 'url', content: test.fullUrl },
    { key: 'title', content: title },
    { key: 'description', content: description },
    { key: 'type', content: 'website' },
    { key: 'image', content: test.imageWithSize?.src },
    { key: 'siteName', content: t('meta', 'site.name') },
    { key: 'brand', content: test.product?.advizedBrand },
    { key: 'priceAmount', content: test.product?.priceSummary.regular },
    { key: 'priceCurrency', content: market.currency },
  ];

  const structuredData = {
    '@context': 'http://schema.org',
    '@type': 'Product',
    name: test.product?.name,
    description,
    lowPrice: test.product?.priceSummary.regular,
    image: [test.product?.media.first],
    datePublished: new Date(parseInt(test.publishedAt, 10)).toISOString(),
    offers: {
      '@type': 'Offer',
      url: `${market.domain}  ${test.product?.pathName}`,
      price: test.product?.priceSummary.regular,
      priceCurrency: market.currency,
    },
    brand: {
      '@type': 'Brand',
      name: test.product?.advizedBrand?.replace(/<(.|\n)*?>/g, ''),
    },
    review: {
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: test.grade / 2,
        bestRating: 5,
        worstRating: 1,
      },
      author: {
        '@type': 'Person',
        name: test.expert.name,
      },
      reviewBody: test.suitedForLong?.replace(/<(.|\n)*?>/g, ''),
      positiveNotes: {
        '@type': 'ItemList',
        itemListElement: test.plus?.map(val => val?.replace(/<(.|\n)*?>/g, '')),
        itemListOrder: 'https://schema.org/ItemListOrderDescending',
      },
      negativeNotes: {
        '@type': 'ItemList',
        itemListElement: test.minus?.map(val =>
          val?.replace(/<(.|\n)*?>/g, '')
        ),
        itemListOrder: 'https://schema.org/ItemListOrderDescending',
      },
      video: {
        '@type': 'VideoObject',
        name: test.product?.name,
        description,
        thumbnailurl: test.video?.thumbnail,
        uploadDate: new Date(parseInt(test.publishedAt, 10)).toISOString(),
        embedUrl: `https://www.youtube.com/watch?v=${test.video?.id}`,
      },
    },
  };

  return {
    description,
    title,
    canonical: test.fullUrl,
    openGraph,
    structuredData,
  };
};

export default expertTest;
